import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, Link as LinkIcon, Library, CheckCircle, Play } from 'lucide-react';
import { getEmbedUrl, isDirectVideoFile, buildAssetUrl } from './LessonPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline "test before publish" player — verifies the chosen URL actually plays.
const VideoTestPreview = ({ url }) => {
  const direct = isDirectVideoFile(url);
  const embed = getEmbedUrl(url);
  const recognized = direct || embed !== url;
  if (!recognized) {
    return (
      <div
        className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2"
        data-testid="lesson-video-test-warning"
      >
        This link isn&apos;t a recognized YouTube / Vimeo / video file URL — members would see a
        black box. Paste a standard YouTube link (watch, share, Shorts, or Live) or an MP4 URL.
      </div>
    );
  }
  return (
    <div
      className="aspect-video w-full max-w-md rounded-md overflow-hidden border border-border bg-black"
      data-testid="lesson-video-test-player"
    >
      {direct ? (
        <video controls className="w-full h-full" src={buildAssetUrl(url)} />
      ) : (
        <iframe
          src={embed}
          title="Video test playback"
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
};

/**
 * LessonVideoSource — 3-way video source picker for a lesson.
 *
 * Modes:
 *   • URL      — paste a YouTube or Vimeo link
 *   • Upload   — chunked upload of MP4 to /api/media/upload
 *   • Library  — pick from the creator's own Video Library
 *
 * Emits the resolved URL string via onChange.
 */
export default function LessonVideoSource({ value, onChange, getAuthHeader }) {
  const initialMode = () => {
    if (!value) return 'url';
    if (value.startsWith('/api/uploads/') || value.startsWith('/api/media/')) return 'upload';
    return 'url';
  };
  const [mode, setMode] = useState(initialMode);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (mode !== 'library') return;
    setLoadingLibrary(true);
    axios
      .get(`${API_URL}/api/video-library/my-videos`, { headers: getAuthHeader() })
      .then((r) => setLibraryVideos(r.data || []))
      .catch(() => toast.error('Could not load your video library'))
      .finally(() => setLoadingLibrary(false));
  }, [mode]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const MAX_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
    if (file.size > MAX_BYTES) {
      toast.error(`File is too large (${(file.size / (1024 * 1024)).toFixed(0)} MB). Max 2 GB.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    const headers = getAuthHeader();
    try {
      // 1) Start a chunked upload session
      const startRes = await axios.post(
        `${API_URL}/api/media/upload/start`,
        { filename: file.name, file_size: file.size, media_type: 'video' },
        { headers }
      );
      const uploadId = startRes.data.upload_id;

      // 2) Upload in 4 MB chunks — small enough to sail through any ingress cap
      const CHUNK_SIZE = 4 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const fd = new FormData();
        fd.append('chunk_index', String(i));
        fd.append('total_chunks', String(totalChunks));
        fd.append('chunk', chunk, `chunk_${i}`);
        await axios.post(
          `${API_URL}/api/media/upload/${uploadId}/chunk`,
          fd,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      // 3) Finalize — server assembles chunks and returns the stream URL
      const finalRes = await axios.post(
        `${API_URL}/api/media/upload/${uploadId}/complete`,
        {},
        { headers }
      );
      onChange(finalRes.data?.url || '');
      toast.success('Video uploaded');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Upload failed';
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const modeTab = (id, label, Icon) => (
    <button
      type="button"
      onClick={() => setMode(id)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        mode === id ? 'bg-brand-gold text-black' : 'text-gray-300 hover:text-white hover:bg-white/5'
      }`}
      data-testid={`lesson-video-source-${id}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <div className="space-y-2" data-testid="lesson-video-source">
      <div className="inline-flex gap-1 rounded-lg bg-white/5 p-1">
        {modeTab('url', 'URL', LinkIcon)}
        {modeTab('upload', 'Upload MP4', Upload)}
        {modeTab('library', 'My Library', Library)}
      </div>

      {mode === 'url' && (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste a YouTube or Vimeo URL"
          className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground"
          data-testid="lesson-video-url-input"
        />
      )}

      {mode === 'upload' && (
        <div className="space-y-2">
          {value && value.startsWith('/api/') && !uploading && (
            <div className="flex items-center gap-2 text-xs text-emerald-400">
              <CheckCircle className="w-3.5 h-3.5" /> Uploaded — {value.split('/').pop()}
            </div>
          )}
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={handleUpload}
              disabled={uploading}
              className="text-xs text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-brand-gold file:text-black file:font-semibold file:cursor-pointer"
              data-testid="lesson-video-file-input"
            />
            {uploading && (
              <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> {progress}%
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-500">MP4, MOV, or WEBM · max 2 GB · uploaded in 4 MB chunks</p>
        </div>
      )}

      {mode === 'library' && (
        <div className="space-y-2">
          {loadingLibrary ? (
            <div className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading your videos…</div>
          ) : libraryVideos.length === 0 ? (
            <p className="text-xs text-gray-500">You haven&apos;t uploaded any videos yet. Add one in Video Library first.</p>
          ) : (
            <select
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground"
              data-testid="lesson-video-library-select"
            >
              <option value="">— Pick a video from your library —</option>
              {libraryVideos.map((v) => {
                const url = v.video_type === 'youtube'
                  ? v.video_url
                  : `${API_URL}/api/video-library/${v.id}/stream`;
                return (
                  <option key={v.id} value={url}>
                    {v.title} {v.status === 'approved' ? '✓' : `(${v.status})`}
                  </option>
                );
              })}
            </select>
          )}
        </div>
      )}

      {value && (
        <div className="space-y-2 pt-1">
          <button
            type="button"
            onClick={() => setShowTest((s) => !s)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-gold hover:underline"
            data-testid="lesson-video-test-toggle"
          >
            <Play className="w-3.5 h-3.5" /> {showTest ? 'Hide test player' : 'Test playback'}
          </button>
          {showTest && <VideoTestPreview url={value} />}
        </div>
      )}
    </div>
  );
}
